import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle
} from "@material-ui/core";
import MyTypography from "@saleor/components/MyTypography";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import { ITEM_STATUS } from "@saleor/createOrChangeNotRefContract/constants";
import { makeStyles } from "@saleor/macaw-ui";
import { FormikProps } from "formik";
import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";

import DialogWarning from "../DialogWarning/DialogWarningPopup";

interface DialogConfirmWarningProps {
  open: boolean;
  loading?: boolean;
  onCancel: () => void;
  onConfirm: (value) => void;
  formikCPItemDetails: FormikProps<any>;
  formikItemDetails: FormikProps<any>;
  formikHeaderDetails: FormikProps<any>;
  selectedOrderLines: string[];
}

const useStyles = makeStyles(
  theme => ({
    root: {
      "& .MuiPaper-root": {
        maxWidth: 440,
        width: "100%",
        borderRadius: 16
      },
      "& .MuiDialogTitle-root": {
        padding: 24
      },
      "& .MuiDialogContent-root": {
        paddingTop: 5,
        marginBottom: "1rem",
        "& .MuiFormControl-root": {
          height: 40
        }
      },
      "& .MuiDialogActions-root": {
        borderTop: 0,
        paddingBottom: 24,
        columnGap: 16
      },
      "& .MuiButton-root": {
        fontSize: "1.8rem",
        "& .MuiTypography-root": {
          fontSize: "1.8rem"
        }
      }
    },
    btnAction: {
      height: 48,
      "&.MuiButton-containedPrimary .MuiTypography-root": {
        color: "#ffffff"
      }
    },
    selectCancel: {
      "& .MuiSelect-root b": {
        fontWeight: 400
      }
    },
    title: {
      color: theme.palette.error.light
    },
    titleContent: {
      marginBottom: "2.4rem"
    }
  }),
  { name: "DialogConfirmDelete" }
);

const DialogConfirmCancelDeleteItem: React.FC<DialogConfirmWarningProps> = ({
  onCancel,
  onConfirm,
  open,
  loading,
  formikItemDetails,
  formikHeaderDetails,
  selectedOrderLines
}) => {
  const classes = useStyles();
  const intl = useIntl();
  const [selectedValue, setSelectedValue] = useState<string>("Cancel");
  useEffect(() => {
    setSelectedValue("Cancel");
  }, [open]);

  const orderType = formikHeaderDetails?.values?.orderType;
  const [showWarningDialog, setShowWarningDialog] = useState(false);
  const setReasonForCancel = e => {
    setSelectedValue(e.target.value);
  };

  const getReasonChoicesForReject = selectedOrderLines => {
    const reasonChoicesForReject = [];
    const selectedOrderLineData = [];
    const parentChildOrderItemsDict = {};
    const parentChildOriginalItemDict = {};
    for (const item of formikItemDetails.values.lines) {
      if (selectedOrderLines.includes(item.itemNo)) {
        selectedOrderLineData.push({
          itemNo: item.itemNo,
          itemStatusEn: item.itemStatusEn,
          bomFlag: item.bomFlag,
          parentItemNo: item.parentItemNo
        });
      }

      if (
        item.bomFlag &&
        item.parentItemNo &&
        selectedOrderLines.includes(item.parentItemNo)
      ) {
        const parentItemNo = item.parentItemNo;
        parentChildOriginalItemDict[parentItemNo] =
          parentChildOriginalItemDict[parentItemNo] || [];
        parentChildOriginalItemDict[parentItemNo].push(item.itemNo);
      }
    }
    for (const orderLine of selectedOrderLineData) {
      if (
        orderLine.bomFlag &&
        orderLine.parentItemNo &&
        selectedOrderLines.includes(orderLine.parentItemNo)
      ) {
        const parentItemNo = orderLine.parentItemNo;
        parentChildOrderItemsDict[parentItemNo] =
          parentChildOrderItemsDict[parentItemNo] || [];
        parentChildOrderItemsDict[parentItemNo].push(orderLine.itemNo);
      }
    }

    const isAllChildSelectedForDelete = () => {
      for (const parent in parentChildOrderItemsDict) {
        if (parentChildOrderItemsDict.hasOwnProperty(parent)) {
          const childItems = parentChildOrderItemsDict[parent];
          const originalChildList = parentChildOriginalItemDict[parent] || [];
          if (!originalChildList.every(child => childItems.includes(child))) {
            return false;
          }
        }
      }
      return true;
    };

    const hasValidStatesForCancel = selectedOrderLineData.every(
      line =>
        line.itemStatusEn === ITEM_STATUS.ITEM_CREATED ||
        line.itemStatusEn === ITEM_STATUS.PARTIAL_DELIVERY
    );

    const hasValidStateForDelete =
      orderType !== "ZP20" &&
      selectedOrderLineData.every(
        line => line.itemStatusEn === ITEM_STATUS.ITEM_CREATED
      ) &&
      isAllChildSelectedForDelete();

    if (hasValidStatesForCancel && hasValidStateForDelete) {
      reasonChoicesForReject.push(
        { value: "Cancel", label: <b>Cancelled 93</b> },
        { value: "Delete", label: <b>Delete</b> }
      );
    } else if (!hasValidStatesForCancel && hasValidStateForDelete) {
      reasonChoicesForReject.push({ value: "Delete", label: <b>Delete</b> });
    } else if (hasValidStatesForCancel && !hasValidStateForDelete) {
      reasonChoicesForReject.push({
        value: "Cancel",
        label: <b>Cancelled 93</b>
      });
    }
    return reasonChoicesForReject;
  };

  const reasonChoicesForReject = useMemo(() => {
    if (selectedOrderLines && selectedOrderLines.length > 0) {
      return getReasonChoicesForReject(selectedOrderLines);
    }
    return [];
  }, [selectedOrderLines, formikItemDetails, orderType]);

  const handleSubmit = () => {
    if (selectedValue) {
      if (selectedValue === "Delete") {
        for (const item of formikItemDetails.values.lines) {
          if (
            selectedOrderLines.includes(item.itemNo) &&
            item.parentItemNo &&
            !selectedOrderLines.includes(item.parentItemNo)
          ) {
            onCancel(); // for close the ist dialog box
            setShowWarningDialog(true);
            return;
          }
        }
        onConfirm(selectedValue);
      } else {
        onConfirm(selectedValue);
      }
    }
  };
  const handleCloseWarningDialog = () => {
    setShowWarningDialog(false); // Close the warning dialog
  };

  return (
    <>
      <DialogWarning
        isOpen={showWarningDialog} // Pass the state to control the dialog
        title={"ไม่สามารถทำรายการได้"}
        content={"เนื่องจาก Mat. ลูก ไม่สามารถ Delete ได้"}
        okText={"ปิดหน้าต่าง"}
        onClose={handleCloseWarningDialog} // Close the dialog when "ปิดหน้าต่าง" is clicked
      />
      <Dialog open={open} onClose={onCancel} className={classes.root}>
        <DialogTitle>
          <MyTypography variant="h6" className={classes.title}>
            Cancel item สินค้าที่เลือก
          </MyTypography>
        </DialogTitle>
        <DialogContent>
          <MyTypography variant={"body2"} className={classes.titleContent}>
            กรุณาระบุ Reason for reject และกด “ยืนยัน” หรือกด “กลับไป”
            เพื่อปิดหน้าต่างนี้
          </MyTypography>
          <MyTypography variant={"subtitle3"}>
            {intl.formatMessage({ defaultMessage: "Reason for reject" })}
          </MyTypography>
          <SingleSelectField
            placeholder={"Select"}
            choices={reasonChoicesForReject}
            onChange={setReasonForCancel}
            value={selectedValue}
            className={classes.selectCancel}
          />
        </DialogContent>
        <DialogActions disableSpacing={true}>
          <Button
            id="backButton"
            color="primary"
            fullWidth={true}
            onClick={onCancel}
            disabled={loading}
            variant="outlined"
            className={classes.btnAction}
          >
            <MyTypography variant="body1">
              {intl.formatMessage({ defaultMessage: "กลับไป" })}
            </MyTypography>
          </Button>
          <Button
            id="confirmButton"
            color="primary"
            variant="contained"
            disabled={loading}
            className={classes.btnAction}
            fullWidth={true}
            onClick={handleSubmit}
          >
            <MyTypography variant="body1">
              {intl.formatMessage({ defaultMessage: "ยืนยัน" })}
            </MyTypography>
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default DialogConfirmCancelDeleteItem;
